import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Button, EmptyState, failedEmptyState } from "../../../components/ui";
import { wayInState } from "../../../components/layout/wayInShell";
import { ROUTES, billingCheckoutPath, trialDetailsPath } from "../../../routes/paths";
import PlanDurations, {
  DurationChoice,
} from "../components/PlanDurations";
import PlanShowcase from "../components/PlanShowcase";
import TrialLayout from "../components/TrialLayout";
import { PRO_FEATURES } from "../data/subscriptionPlans";
import { usePlans } from "../hooks/usePlans";
import { useTrialAccess } from "../hooks/useTrialAccess";
import { TRIAL_DAYS } from "../trialState";
import type { SubscriptionPlan } from "../types/billing.types";

/**
 * Which term the trial is started against when the fortnight itself is what
 * was picked.
 *
 * The trial row commits to no term — nothing is charged and nothing renews —
 * but the step after it is keyed by one, because the screen that confirms the
 * fortnight also says what the plan costs once it is over. The shortest, so
 * nothing over-states what somebody has agreed to by pressing a free button;
 * the price list offers it first and it is the one every workspace can see.
 */
const TRIAL_TERM: DurationChoice = "monthly";

/**
 * The first screen a new account sees: one product, and every way to start it.
 *
 * ## Why this is not the Billings tab
 *
 * The same product is on sale in Settings › Billing & plans, as the same panel
 * beside the same list of terms. Here the list has one more row at the head of
 * it — the fourteen free days — because the question a brand new account is
 * being asked is not "which term" but "how do you want to start", and the
 * trial is one of the answers to it. Everything below that row is identical,
 * down to the component: see `PlanDurations`, which draws both screens.
 *
 * That shared list is the whole point. The offer and the shop used to be two
 * layouts quoting the same price list in two different shapes — a card with a
 * cycle switch here, two cards there — and keeping them in step was something
 * somebody had to remember. Now a term the backend starts pricing appears on
 * both, worded the same way, because there is one list.
 *
 * Every figure is the price list's — `GET /api/v1/billing/plans/`, through
 * `usePlans`. Nothing here writes a price down, and a list that fails to load
 * draws no rows at all: an invented price is worse than an honest "we could
 * not load this".
 *
 * No sidebar: this screen is outside `AppLayout` on purpose. The rail appears
 * for the first time on the dashboard, once the trial has actually started.
 */
const TrialPlanPage: React.FC = () => {
  const navigate = useNavigate();
  const priceList = usePlans();

  // Whether this workspace is being shown the offer at all — the server's
  // `can_start_trial`, off `GET /billing/subscription/`. See the guard below.
  const gate = useTrialAccess();

  /**
   * What the list is set to, opening on the trial.
   *
   * The one place in the app where a row *is* picked for somebody, and the
   * reason is that this row costs nothing and commits to nothing: there is no
   * decision being made on their behalf, only the free option put in front of
   * them. The Billings tab opens on nothing picked, because there every row is
   * a charge.
   */
  const [chosen, setChosen] = useState<DurationChoice | null>("trial");

  /**
   * Nothing is drawn until the price list has answered.
   *
   * Not a placeholder, and not the list half-built: either is a box of one
   * height standing where a box of another height is about to go, and this
   * screen is centred in the window — so the shorter thing sits in the middle
   * and then *opens* to full height as the figures land, which is the one
   * movement there is no excuse for on a page somebody is reading.
   */
  const deciding = gate.status === "loading";

  /**
   * The offer is over before it is drawn: this workspace may not start a trial.
   *
   * Because it is already paying, because a trial is running, or because it has
   * had one — the server says which in `access_source`, and none of the three
   * is a reason to show somebody fourteen free days they cannot have. Straight
   * to the app instead, with `replace` so Back does not land here again.
   *
   * Asked *before* the price list is waited on, and answered as soon as it
   * lands: this screen is where every way into the app now points, so a
   * subscriber signing in passes through it, and there is no sense making them
   * wait on the price of a plan they are not being shown.
   *
   * A failed read is deliberately *not* treated as a refusal. Not knowing is
   * not the same as knowing they have had their trial, and the same caution the
   * subscription gate takes over the create buttons applies here: the worst a
   * wrongly-shown offer costs is a refusal at the last step, while a wrongly-
   * hidden one costs somebody the trial they were entitled to.
   */
  if (gate.status === "ready" && !gate.access?.canStartTrial) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  if (deciding || priceList.status === "loading") {
    return (
      <TrialLayout>
        <p role="status" className="sr-only">
          Loading the plans…
        </p>
      </TrialLayout>
    );
  }

  /**
   * Nothing to show: the price list failed, or came back with no term this app
   * can draw a row for. Both are the same thing to the reader, and neither is a
   * page to invent a price for.
   *
   * The trial row is not drawn on its own in that case either, tempting as it
   * is — a fortnight offered with no sign of what it turns into is the part of
   * a free trial people complain about.
   */
  if (priceList.status === "failed" || priceList.plans.length === 0) {
    return (
      <TrialLayout>
        <EmptyState
          {...failedEmptyState(
            "We could not load the plans",
            <Button size="sm" variant="secondary" onClick={priceList.reload}>
              Try again
            </Button>
          )}
        />
      </TrialLayout>
    );
  }

  /** Taking the fortnight: on to the step that collects the billing details
   *  and confirms what happens on each of its three dates. */
  const startTrial = () => navigate(trialDetailsPath(TRIAL_TERM));

  /**
   * Buying a term outright instead.
   *
   * A real answer to the question this list asks, and the trial is not an
   * obligation — somebody who already knows they want the year should not have
   * to take fourteen days first and come back. The checkout is the same one the
   * Billings tab opens, keyed by the term in the URL.
   */
  const buy = (plan: SubscriptionPlan) =>
    // Told where they are coming from, which is the one thing that checkout
    // cannot see for itself: it is the same screen either way, and opened from
    // here it is the second of three steps of a way in rather than a job done
    // from inside the app. The shell draws none of the app around it on the
    // strength of this — no rail, no tab bar, no off-white page — so the offer,
    // the checkout and the confirmation are one flow rather than a white page,
    // then the app, then the app again. See `wayInShell`.
    navigate(billingCheckoutPath(plan.id), { state: wayInState() });

  return (
    <TrialLayout>
      {/* What this screen is, in the reader's terms rather than the product's:
          they have just signed up and are one press from being in. It is the
          page's own heading, so it sits above both panels rather than inside
          either — the picture is as much a part of the offer as the prices.

          It read "You're one step away" until the design named it. The new
          words are plainer and they say what the screen is for, which matters
          more here than warmth: this page is often opened from a link in a
          bio, in an in-app browser, by somebody who has not seen the product
          yet and needs the tab to say what they have landed on.

          The deepest of the brand's teals — the one the plan card's "Active"
          pill is set in, rather than the mid teal a link takes. A heading this
          size in the lighter shade reads as something to press. */}
      <h1 className="text-[26px] min-[1400px]:text-[28px] 3xl:text-[32px] font-semibold leading-tight text-[#00424B]">
        Subscribe Naurist
      </h1>

      {/* The same two columns the Billings tab draws, and for the same reason:
          what the product is on the left, how to start it on the right. Stacked
          below `lg`, panel first, so the page opens on the offer at every
          width.

          Both columns share one height, as they do on that tab: the panel and
          the list beside it end on the same line, and the rows take up
          whatever is left over rather than leaving a band of nothing above
          the button. See `PlanDurations`, which does that filling itself, so
          the only thing this grid has to do is stop holding the column to its
          own height. */}
      <div className="mt-6 3xl:mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 3xl:gap-8">
        <PlanShowcase features={PRO_FEATURES} />

        <PlanDurations
          plans={priceList.plans}
          value={chosen}
          onChange={setChosen}
          onBuy={buy}
          // A pinned Buy row across the foot of the phone, as on the
          // Billings tab — held against the window, because on a phone this
          // list is stacked under the panel and a bar tied to the list would
          // not be on screen until the panel had been scrolled past.
          //
          // The gutter is this shell's own, so the button lines up with the
          // cards above it while the glass runs to the edge of the screen.
          // `sm:px-12` because that is what `TrialLayout` widens to, and a
          // phone wide enough to reach it is still a phone. See `ctaGutter`.
          pinCta
          ctaGutter="px-page sm:px-12"
          trial={{
            // Named as the thing rather than as the act: the row is one of
            // four answers to "how do you want to start", and the other three
            // are called "1 month" and "3 month" rather than "Buy 1 month".
            title: `${TRIAL_DAYS} day trial`,
            // Half the sentence this used to carry has gone with the thing it
            // described. "Start free, pay after 14 days" promised a charge at
            // the end of the fortnight, and there is none: the trial takes no
            // payment detail and converts into nothing — it ends, Pro
            // pauses, and nobody is billed unless they go and buy a term. See
            // `TrialSummary`, which lost the same clause.
            note: "No payment detail required",
            price: "Free",
            cta: `Start ${TRIAL_DAYS} days free trial`,
            onStart: startTrial,
          }}
        />
      </div>
    </TrialLayout>
  );
};

export default TrialPlanPage;
